import React, { useCallback } from 'react';
import { Grid, TextInput, NumberInput, Slider, Text, Box, Textarea, Button, Group } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconSitemap } from '@tabler/icons-react';
import type { TaskSnapshot } from '../../../stores/types';
import type { TaskDetailsEditableField } from '../../../hooks/useTaskDetailsDraft';
import type { QuantityField } from '../../../gantt/taskQuantity';
import { effectiveUom, formatInheritedUomLabel } from '../../../gantt/effectiveUom';
import { isConvertedTask, quantityProgressPercent } from '../../../gantt/taskQuantity';
import { formatDateForBackend, parseDateSafe, resolveTaskName } from '../../../utils/utils';
import { GANTT_DATE_DISPLAY_FORMAT } from '../../../gantt/ganttDateFormat';
import {
  formatTaskTypeLabel,
  taskDetailsCompactLabelProps,
  taskDetailsFieldLabel,
  taskDetailsReadOnlyFieldStyles,
} from './taskDetailsFormUtils';
import { TaskTrackingDetailsSection } from './TaskTrackingDetailsSection';
import { TaskBaselineComparisonSection } from './TaskBaselineComparisonSection';
import { TaskLinkedIssuesSection } from './TaskLinkedIssuesSection';

export interface TaskDetailsFormBodyProps {
  taskIdDisplay: string;
  draft: TaskSnapshot;
  readOnlyForNonLeaf: boolean;
  resolvedLocationValue: string;
  draftPredecessors: string;
  successorText: string;
  predecessorFieldLabel: string;
  successorFieldLabel: string;
  updateDraftField: (field: TaskDetailsEditableField, value: unknown) => void;
  updateDraftQuantity: (field: QuantityField, value: string | number | null) => void;
  quantityErrors?: Partial<Record<QuantityField, string>>;
  getTask: (id: string) => TaskSnapshot | null | undefined;
  updateDraftPredecessors: (value: string) => void;
  commitDraftPredecessors: (value?: string) => void;
  /** When set, show read-only Current vs Baseline N summary. */
  compareBaselineIndex?: number | null;
  /** Increment to scroll Issues / Hindrances into view. */
  scrollToLinkedIssuesRequest?: number;
  /** Open predecessor task-tree picker. */
  onPickPredecessors?: () => void;
  /** Open Assign users drawer for this task. */
  onOpenAssignUsers?: () => void;
  /** Short summary of assignees for this task (optional). */
  assigneesSummary?: string;
}

export function TaskDetailsFormBody({
  taskIdDisplay,
  draft,
  readOnlyForNonLeaf,
  resolvedLocationValue,
  draftPredecessors,
  successorText,
  predecessorFieldLabel,
  successorFieldLabel,
  updateDraftField,
  updateDraftQuantity,
  quantityErrors,
  getTask,
  updateDraftPredecessors,
  commitDraftPredecessors,
  compareBaselineIndex = null,
  scrollToLinkedIssuesRequest = 0,
  onPickPredecessors,
  onOpenAssignUsers,
  assigneesSummary,
}: TaskDetailsFormBodyProps) {
  const isLeaf = !readOnlyForNonLeaf;
  const converted = isConvertedTask(draft.unitActivityDetails);
  const inheritedUom = effectiveUom(draft.id, (id) => {
    const task = getTask(id);
    if (id !== draft.id) return task;
    return { ...(task ?? draft), uom: draft.uom ?? null };
  });
  const inheritedUomLabel = formatInheritedUomLabel(inheritedUom);
  const quantityProgress = isLeaf
    ? quantityProgressPercent(draft.totalQuantity, draft.achievedQuantity)
    : null;
  const progressReadOnly = readOnlyForNonLeaf || quantityProgress != null;
  const progressValue = quantityProgress ?? (draft.percentComplete || 0);

  const setQuantityNumber = useCallback(
    (field: 'totalQuantity' | 'achievedQuantity', value: string | number) => {
      if (value === '') {
        updateDraftQuantity(field, null);
        return;
      }
      const n = typeof value === 'number' ? value : Number(value);
      updateDraftQuantity(field, Number.isFinite(n) ? n : null);
    },
    [updateDraftQuantity],
  );

  const setField = useCallback(
    (field: TaskDetailsEditableField, value: unknown) => {
      if (readOnlyForNonLeaf && field !== 'taskName' && field !== 'notes') return;
      updateDraftField(field, value);
    },
    [readOnlyForNonLeaf, updateDraftField]
  );

  const parseDate = useCallback((dateValue: Date | string | null): Date | null => {
    if (!dateValue) return null;
    try {
      const parsed = dateValue instanceof Date ? dateValue : (parseDateSafe(dateValue) ?? null);
      return !parsed || Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  }, []);

  const handleDateChange = useCallback(
    (field: 'start' | 'finish') => (dateValue: Date | string | null) => {
      const parsed = parseDate(dateValue);
      setField(field, parsed ? formatDateForBackend(parsed) : null);
    },
    [parseDate, setField]
  );

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <TextInput
            label={taskDetailsFieldLabel(
              taskIdDisplay ? `Task Name (${taskIdDisplay})` : 'Task Name',
            )}
            value={resolveTaskName(draft)}
            onChange={(e) => setField('taskName', e.currentTarget.value)}
            placeholder="Enter task name"
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            label={taskDetailsFieldLabel('Critical')}
            value={draft.critical ? 'Yes' : 'No'}
            readOnly
            styles={taskDetailsReadOnlyFieldStyles}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            label={taskDetailsFieldLabel('Task type')}
            value={formatTaskTypeLabel(draft.taskType)}
            readOnly
            styles={taskDetailsReadOnlyFieldStyles}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <TextInput
            label={taskDetailsFieldLabel('Location')}
            value={resolvedLocationValue}
            readOnly
            styles={taskDetailsReadOnlyFieldStyles}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Text size="sm" fw={500} mb={4} {...taskDetailsCompactLabelProps}>
            Assignees
          </Text>
          <Text size="xs" c="dimmed" mb={onOpenAssignUsers ? 6 : 0}>
            {assigneesSummary?.trim()
              ? assigneesSummary
              : 'No assignees listed for this task yet.'}
          </Text>
          {onOpenAssignUsers ? (
            <Button size="compact-xs" variant="light" onClick={onOpenAssignUsers}>
              Assign users
            </Button>
          ) : null}
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            label={taskDetailsFieldLabel(predecessorFieldLabel)}
            value={draftPredecessors}
            placeholder={
              readOnlyForNonLeaf
                ? undefined
                : 'e.g. 1.2FS, 1.3SS+2, 1.4FS-1 (Sr No / WBS + type + lag/lead)'
            }
            readOnly={readOnlyForNonLeaf}
            styles={readOnlyForNonLeaf ? taskDetailsReadOnlyFieldStyles : undefined}
            onChange={
              readOnlyForNonLeaf
                ? undefined
                : (e) => updateDraftPredecessors(e.currentTarget.value)
            }
            onBlur={
              readOnlyForNonLeaf
                ? undefined
                : (e) => commitDraftPredecessors(e.currentTarget.value)
            }
            labelProps={taskDetailsCompactLabelProps}
          />
          {!readOnlyForNonLeaf && onPickPredecessors ? (
            <Group mt={6}>
              <Button
                size="compact-xs"
                variant="light"
                leftSection={<IconSitemap size={12} />}
                onClick={onPickPredecessors}
              >
                Pick predecessors
              </Button>
            </Group>
          ) : null}
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            label={taskDetailsFieldLabel(successorFieldLabel)}
            value={successorText}
            readOnly
            styles={taskDetailsReadOnlyFieldStyles}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <DateInput
            label={taskDetailsFieldLabel('Planned start')}
            value={parseDate(draft.start)}
            onChange={handleDateChange('start')}
            placeholder="Select start date"
            valueFormat={GANTT_DATE_DISPLAY_FORMAT}
            readOnly={readOnlyForNonLeaf}
            styles={readOnlyForNonLeaf ? taskDetailsReadOnlyFieldStyles : undefined}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput
            label={taskDetailsFieldLabel('Planned duration')}
            value={draft.duration || 0}
            onChange={(value) => setField('duration', value)}
            placeholder="Days"
            suffix=" days"
            min={0}
            step={1}
            hideControls
            readOnly={readOnlyForNonLeaf}
            styles={readOnlyForNonLeaf ? taskDetailsReadOnlyFieldStyles : undefined}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <DateInput
            label={taskDetailsFieldLabel('Planned finish')}
            value={parseDate(draft.finish)}
            onChange={handleDateChange('finish')}
            placeholder="Select finish date"
            valueFormat={GANTT_DATE_DISPLAY_FORMAT}
            readOnly={readOnlyForNonLeaf}
            styles={readOnlyForNonLeaf ? taskDetailsReadOnlyFieldStyles : undefined}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={taskDetailsFieldLabel('UOM')}
            value={draft.uom ?? ''}
            placeholder={inheritedUomLabel ?? 'e.g., m3, sqm, %'}
            error={quantityErrors?.uom}
            onChange={(e) => updateDraftQuantity('uom', e.currentTarget.value)}
            labelProps={taskDetailsCompactLabelProps}
          />
          {inheritedUomLabel ? (
            <Text size="xs" c="dimmed" mt={4}>
              {inheritedUomLabel}
            </Text>
          ) : null}
        </Grid.Col>
        {isLeaf ? (
          <Grid.Col span={6}>
            <NumberInput
              label={taskDetailsFieldLabel('Total quantity')}
              value={draft.totalQuantity ?? ''}
              onChange={(value) => setQuantityNumber('totalQuantity', value)}
              min={0}
              hideControls
              error={quantityErrors?.totalQuantity}
              labelProps={taskDetailsCompactLabelProps}
            />
          </Grid.Col>
        ) : null}
        {isLeaf ? (
          <Grid.Col span={6}>
            <NumberInput
              label={taskDetailsFieldLabel(
                converted ? 'Achieved quantity (Captured through DPR)' : 'Achieved quantity',
              )}
              value={draft.achievedQuantity ?? ''}
              onChange={(value) => setQuantityNumber('achievedQuantity', value)}
              min={0}
              hideControls
              readOnly={converted}
              disabled={converted}
              error={quantityErrors?.achievedQuantity}
              styles={converted ? taskDetailsReadOnlyFieldStyles : undefined}
              labelProps={taskDetailsCompactLabelProps}
            />
          </Grid.Col>
        ) : null}

        <Grid.Col span={12}>
          <Box mt="xs" mb="md" pb="xs">
            <Text size="sm" fw={500} mb={6} {...taskDetailsCompactLabelProps}>
              Progress
            </Text>
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <Box style={{ flex: 1, minWidth: 0, paddingTop: 6, paddingBottom: 14 }}>
                <Slider
                  value={progressValue}
                  onChange={(value) => setField('percentComplete', value)}
                  min={0}
                  max={100}
                  step={1}
                  disabled={progressReadOnly}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' },
                  ]}
                  aria-label="Progress"
                />
              </Box>
              <NumberInput
                value={progressValue}
                onChange={(value) => {
                  const n = typeof value === 'number' ? value : Number(value);
                  const clamped = Number.isFinite(n)
                    ? Math.min(100, Math.max(0, Math.trunc(n)))
                    : 0;
                  setField('percentComplete', clamped);
                }}
                min={0}
                max={100}
                step={1}
                suffix="%"
                hideControls
                w={80}
                disabled={progressReadOnly}
                readOnly={progressReadOnly}
                styles={progressReadOnly ? taskDetailsReadOnlyFieldStyles : undefined}
                aria-label="Progress percent"
              />
            </Group>
          </Box>
        </Grid.Col>

        <Grid.Col span={6}>
          <DateInput
            label={taskDetailsFieldLabel('Actual Start')}
            value={parseDate(draft.actualStart ?? null)}
            placeholder="Select actual start date"
            valueFormat={GANTT_DATE_DISPLAY_FORMAT}
            readOnly
            styles={taskDetailsReadOnlyFieldStyles}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <DateInput
            label={taskDetailsFieldLabel('Actual Finish')}
            value={parseDate(draft.actualFinish ?? null)}
            placeholder="Select actual finish date"
            valueFormat={GANTT_DATE_DISPLAY_FORMAT}
            readOnly
            styles={taskDetailsReadOnlyFieldStyles}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput
            label={taskDetailsFieldLabel('Actual duration')}
            value={draft.actualDuration ?? ''}
            placeholder="Enter actual duration"
            min={0}
            readOnly
            styles={taskDetailsReadOnlyFieldStyles}
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Textarea
            label={taskDetailsFieldLabel('Notes / Remarks')}
            value={draft.notes ?? ''}
            onChange={(e) => setField('notes', e.currentTarget.value)}
            placeholder="Add notes or remarks"
            minRows={3}
            autosize
            labelProps={taskDetailsCompactLabelProps}
          />
        </Grid.Col>
      </Grid>

      <TaskTrackingDetailsSection unitActivityDetails={draft.unitActivityDetails} />

      {compareBaselineIndex != null ? (
        <TaskBaselineComparisonSection
          draft={draft}
          compareBaselineIndex={compareBaselineIndex}
        />
      ) : null}

      <TaskLinkedIssuesSection
        linkedIssues={draft.linkedIssues}
        openIssueCount={draft.openIssueCount}
        scrollIntoViewRequest={scrollToLinkedIssuesRequest}
      />
    </>
  );
}
